import axios from "axios";
import { apiUrlRoot } from "@/vars";

axios.defaults.withXSRFToken = true;
let unauthorizedHandler = null;

const clearStoredAuthentication = () => {
  localStorage.removeItem("authenticated");
  localStorage.removeItem("user");
  localStorage.removeItem("roles");
};

const notifyUnauthorized = () => {
  clearStoredAuthentication();

  if (typeof unauthorizedHandler === "function") {
    unauthorizedHandler();
  }
};

export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = typeof handler === "function" ? handler : null;
};

const apiClient = axios.create({
  baseURL: apiUrlRoot,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    Accept: "application/json",
  },
});

const responseErrorMessage = (
  error,
  fallback = "The request could not be completed.",
) => {
  const message = String(error?.response?.data?.message ?? "").trim();
  if (message) {
    return message;
  }

  const errorMessage = String(error?.response?.data?.error ?? "").trim();
  if (errorMessage) {
    return errorMessage;
  }

  const errors = error?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors)
      .flat()
      .find((value) => String(value ?? "").trim() !== "");

    if (first) {
      return String(first);
    }
  }

  return fallback;
};

// Log every response status to aid debugging
apiClient.interceptors.response.use(
  function (response) {
    const { method, url } = response.config ?? {};
    console.log(`[API ${method?.toUpperCase()} ${url}] -> ${response.status}`);
    return response;
  },
  async function (error) {
    const { method, url } = error.config ?? {};
    const status = error.response?.status ?? "NETWORK_ERROR";
    const suppressErrorAlert = Boolean(error.config?.suppressErrorAlert);
    console.log(`[API ${method?.toUpperCase()} ${url}] -> ${status}`);

    if (
      error.response?.status === 419 &&
      !error.config?.csrfRetryAttempted &&
      !error.config?.skipCsrfRetry
    ) {
      try {
        await apiClient.get("/sanctum/csrf-cookie", {
          skipCsrfRetry: true,
          suppressErrorAlert: true,
        });

        return apiClient.request({
          ...error.config,
          csrfRetryAttempted: true,
        });
      } catch {
        return Promise.reject(error);
      }
    }

    if (error.response && error.response.status === 401) {
      notifyUnauthorized();
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 419) {
      notifyUnauthorized();
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 403) {
      if (!suppressErrorAlert) {
        alert(responseErrorMessage(error, "Access Denied"));
      }
    }

    if (error.response && error.response.status === 422) {
      if (!suppressErrorAlert) {
        alert(responseErrorMessage(error, "Some details need checking."));
      }
      return Promise.reject(error);
    }

    // Network Error
    if (error.code === "ERR_NETWORK") {
      return Promise.reject(error);
    }

    if (error.code === "ERR_BAD_REQUEST") {
      console.log("API request rejected", { method, url, status });
    }

    return Promise.reject(error);
  },
);

// Log outgoing requests so we know what was attempted
apiClient.interceptors.request.use(
  function (config) {
    const csrf = document.cookie
      .split("; ")
      .find((item) => item.startsWith("NEPTUNE-AGENT-CSRF="))
      ?.split("=")[1];
    if (csrf) {
      config.headers["X-Neptune-CSRF"] = decodeURIComponent(csrf);
      if (
        !["get", "head", "options"].includes(
          String(config.method).toLowerCase(),
        ) &&
        !config.headers["Idempotency-Key"]
      ) {
        config.headers["Idempotency-Key"] = crypto.randomUUID();
      }
    }
    console.log(
      `[API ${config.method?.toUpperCase()} ${config.url}] -> sending`,
    );
    return config;
  },
  function (error) {
    // Do something with request error if needed
    return Promise.reject(error);
  },
);

export default apiClient;
